import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Grid, IconButton, MenuItem, Paper, Select, SelectChangeEvent, Stack } from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as CreateIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Done as SaveIcon,
} from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import { permissionsCheck } from '../../../utils/permission';
import {
  CreateUserDTO,
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetRolesQuery,
  useLazyGetUsersQuery,
  useUpdateUserMutation,
} from '../../../api/controllers/admin-roles';
import { User } from '../../../types/models/roles.model';
import { CreateUserModal, DeleteConfirmModal } from './components';
import { layoutAction } from '../../../store/sideBarStore/layoutStore';
import { useAppActions } from '../../../utils/hooks';

export const Users: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditUsers = permissionsCheck(['WRITE_ROLES']);

  // api
  const [getUsers, { data: users, isLoading: isCountriesLoading }] = useLazyGetUsersQuery();
  const [deleteUser, { isLoading: isDeleteLoading }] = useDeleteUserMutation();
  const [createUser, { isLoading: isCreateLoading }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdateLoading }] = useUpdateUserMutation();
  const { data: roles, isLoading: rolesLoading } = useGetRolesQuery();
  // variables
  const isLoading = isCountriesLoading || isDeleteLoading || isCreateLoading;
  const [activeRow, setActiveRow] = useState<User | null>(null);
  const [editFields, setEditFields] = useState<CreateUserDTO | null>(null);
  const [isEditMode, setEditMode] = useState(false);

  const roleSelectOptions = roles?.roles.map((it) => {
    return (
      <MenuItem key={it.id} value={it.id}>
        {it.name}
      </MenuItem>
    );
  });
  const startEditMode = (user: User) => {
    setEditFields({
      ...user,
      role_id: user.role.id,
    } as unknown as CreateUserDTO);
    setActiveRow(user);
    setEditMode(true);
  };

  const offEditMode = () => {
    setEditFields(null);
    setActiveRow(null);
    setEditMode(false);
  };

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    hasPermissionToEditUsers &&
      setSpeedDialButtonComponent([{ icon: <CreateIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  useEffect(() => {
    getUsers();
  }, []);

  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;
    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // delete user modal logic
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const openDeleteModal = (user: User) => {
    setDeleteModalShown(true);
    setActiveRow(user);
  };
  const closeDeleteModal = () => {
    setDeleteModalShown(false);
    setActiveRow(null);
  };
  // create modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
    setEditFields(null);
  };

  const onCreateUser = async () => {
    if (!editFields) return;

    const args = {
      email: editFields.email,
      role_id: editFields.role_id,
    };

    try {
      await createUser(args).unwrap();
      closeCreateModal();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const deleteUserHandler = async () => {
    if (!activeRow) return;

    const args = { userUid: activeRow.uid, roleId: activeRow.role.id };

    try {
      await deleteUser(args).unwrap();
      closeDeleteModal();
      getUsers();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const updateUserHandler = async () => {
    if (!editFields || !activeRow) return;

    const args = { userUid: activeRow.uid, roleId: editFields.role_id };

    try {
      await updateUser(args).unwrap();
      getUsers();
      offEditMode();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('users-and-roles.users.head-cells.uid') },
    { id: '2', label: t('users-and-roles.users.head-cells.email') },
    { id: '3', label: t('users-and-roles.users.head-cells.role') },
    {
      id: '4',
      label: t('users-and-roles.users.head-cells.actions'),
      hidden: !hasPermissionToEditUsers,
    },
  ];

  const rows: Row[] | undefined = users?.users.map((it) => {
    const { role, email, uid } = it;

    const cells: RowCell[] = [
      {
        name: 'uid',
        value: uid,
      },
      { name: 'email', value: email },
      {
        name: 'roleName',
        value: role.name,
        component:
          isEditMode && activeRow?.uid === uid ? (
            <Select
              name="role_id"
              value={`${editFields?.role_id}`}
              onChange={handleEditFields}
              variant="standard"
              sx={{ width: '80px' }}
              disabled={rolesLoading}
            >
              {roleSelectOptions}
            </Select>
          ) : undefined,
      },
      {
        name: 'action',
        hidden: !hasPermissionToEditUsers,
        component: isEditMode ? (
          activeRow?.uid === uid ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={() => updateUserHandler()}
                disabled={isUpdateLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={() => offEditMode()}
                disabled={isUpdateLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={isLoading}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label="delete"
              color="error"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openDeleteModal(it);
              }}
              disabled={isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Stack sx={{ gap: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Table headCells={headCells} rows={rows} />
          </Paper>
        </Grid>
      </Stack>

      <DeleteConfirmModal
        open={deleteModalShown}
        onClose={closeDeleteModal}
        onSuccess={deleteUserHandler}
        onCancel={closeDeleteModal}
        isLoading={isLoading}
      />

      <CreateUserModal
        open={createModalShown}
        onClose={closeCreateModal}
        onSuccess={onCreateUser}
        onCancel={closeCreateModal}
        isLoading={isLoading}
        fields={editFields}
        handleEditFields={handleEditFields}
      />
    </>
  );
};
